import axios from 'axios';
import AuthService from './AuthService';
import { getPatientMoleMappings, getPatientBaselineSessions, getPatientComparisonHistory } from './moleMappingApi';
import { getABCDE, getSegmentationForAnnotation } from './abcdeAPI';

const API_BASE = process.env.REACT_APP_API_URL;

// Helper function to get auth headers
async function getAuthHeaders() {
  const authService = new AuthService();
  const token = await authService.getAccessToken();
  
  if (!token) {
    throw new Error("No access token available");
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

export interface PatientReportData {
  patientId: string;
  patientName: string;
  reportDate: string;
  summary: {
    totalSessions: number;
    totalMoles: number;
    totalABCDEAssessments: number;
    highRiskMoles: number;
    followupSessions: number;
    lastSessionDate: string;
  };
  sessions: SessionReportData[];
  riskTrends: RiskTrendData[];
  recommendations: string[];
}

export interface SessionReportData {
  sessionId: string;
  sessionDate: string;
  sessionType: 'baseline' | 'followup';
  notes: string;
  macroImages: MacroImageReportData[];
  comparison?: {
    baselineSessionId?: string;
    totalMoles: number;
    newMoles: number;
    changedMoles: number;
    stableMoles: number;
    overallRisk: string;
  };
}

export interface ABCDEReportData {
  assessmentId: string;
  assessmentDate: string;
  scores: {
    asymmetry: number;
    borders: number;
    color: number;
    diameter: number;
    evolution: number;
    total: number;
  };
  riskLevel: 'low' | 'moderate' | 'high';
  notes?: string;
  recommendation?: string;
}

export interface SegmentationReportData {
  analysisDate: string;
  metrics: {
    circularity: number;
    asymmetry: number;
    colorVariation: {
      red: number;
      green: number;
      blue: number;
    };
  };
  visualizations: {
    segmentedPlot?: string;
    colorPlot?: string;
    asymmetryPlot1?: string;
    asymmetryPlot2?: string;
  };
}

export interface AnnotationReportData {
  annotationId: string;
  label: string;
  position: { x: number; y: number; width: number; height: number };
  confidence?: number;
  linkedMicroUrl?: string;
  linkedMicroId?: string;
  abcdeAssessments: ABCDEReportData[];
  segmentation?: SegmentationReportData;
  riskLevel: 'low' | 'moderate' | 'high';
  lastAssessmentDate?: string;
}

export interface MacroImageReportData {
  imageId: string;
  imageUrl: string;
  filePath: string;
  annotations: AnnotationReportData[];
}

export interface RiskTrendData {
  annotationId: string;
  label: string;
  assessments: Array<{
    date: string;
    totalScore: number;
    riskLevel: string;
  }>;
  trend: 'stable' | 'improving' | 'worsening';
}

export async function getPatientReportData(patientId: string): Promise<PatientReportData> {
  try {
    console.log(`Generating comprehensive report for patient: ${patientId}`);
    
    // Fetch all mole mapping sessions
    const sessions = await getPatientMoleMappings(patientId);
    console.log(`Found ${sessions.length} sessions`);
    
    // Fetch baseline sessions for comparison data
    const baselineSessions = await getPatientBaselineSessions(patientId);
    console.log(`Found ${baselineSessions.length} baseline sessions`);
    
    // Fetch comparison history
    let comparisonHistory = [];
    try {
      comparisonHistory = await getPatientComparisonHistory(patientId);
    } catch (error) {
      console.warn('Failed to fetch comparison history:', error);
    }
    
    // Process each session to get detailed data
    const sessionReportData: SessionReportData[] = [];
    let totalMoles = 0;
    let totalABCDEAssessments = 0;
    let highRiskMoles = 0;
    const riskTrends: Map<string, RiskTrendData> = new Map();
    
    for (const session of sessions) {
      console.log(`Processing session: ${session.id}`);
      
      // Get macro images for this session
      const macroImages = await getMacroImagesForSession(session.id);
      console.log(`Found ${macroImages.length} macro images for session ${session.id}`);
      
      const macroImageReportData: MacroImageReportData[] = [];
      
      for (const macroImage of macroImages) {
        // Get annotations for this macro image
        const annotations = await getAnnotationsForMacroImage(macroImage.id);
        console.log(`Found ${annotations.length} annotations for macro image ${macroImage.id}`);
        
        const annotationReportData: AnnotationReportData[] = [];
        
        for (const annotation of annotations) {
          totalMoles++;
          
          // Get ABCDE assessments for this annotation
          const abcdeResults = await getABCDE(annotation.id);
          const abcdeReportData: ABCDEReportData[] = abcdeResults.map(result => ({
            assessmentId: result._additional.id,
            assessmentDate: result.createdAt || new Date().toISOString(),
            scores: {
              asymmetry: result.asymmetry,
              borders: result.borders,
              color: result.color,
              diameter: result.diameter,
              evolution: result.evolution,
              total: result.totalScore
            },
            riskLevel: getRiskLevel(result.totalScore),
            notes: result.notes,
            recommendation: result.recommendation
          }));
          
          totalABCDEAssessments += abcdeReportData.length;
          
          // Check for high risk assessments
          const hasHighRisk = abcdeReportData.some(assessment => assessment.riskLevel === 'high');
          if (hasHighRisk) {
            highRiskMoles++;
          }
          
          // Get segmentation data
          let segmentationData: SegmentationReportData | undefined;
          try {
            const segmentationResults = await getSegmentationForAnnotation(annotation.id);
            if (segmentationResults.length > 0) {
              const latest = segmentationResults[0];
              segmentationData = {
                analysisDate: latest._additional.id ? new Date().toISOString() : new Date().toISOString(),
                metrics: {
                  circularity: latest.circularity || 0,
                  asymmetry: latest.asymmetry || 0,
                  colorVariation: {
                    red: latest.std_dev_r || 0,
                    green: latest.std_dev_g || 0,
                    blue: latest.std_dev_b || 0
                  }
                },
                visualizations: {
                  segmentedPlot: latest.segmentedPlot,
                  colorPlot: latest.colorplot,
                  asymmetryPlot1: latest.assymetryPlot1,
                  asymmetryPlot2: latest.assymetryplot2
                }
              };
            }
          } catch (error) {
            console.warn(`Failed to fetch segmentation for annotation ${annotation.id}:`, error);
          }
          
          // Determine overall risk level for this annotation
          const latestRiskLevel = abcdeReportData.length > 0 
            ? abcdeReportData[abcdeReportData.length - 1].riskLevel 
            : 'low';
          
          // Track risk trends for this annotation
          if (abcdeReportData.length > 0) {
            const trendData: RiskTrendData = {
              annotationId: annotation.id,
              label: annotation.label,
              assessments: abcdeReportData.map(assessment => ({
                date: assessment.assessmentDate,
                totalScore: assessment.scores.total,
                riskLevel: assessment.riskLevel
              })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
              trend: calculateTrend(abcdeReportData)
            };
            riskTrends.set(annotation.id, trendData);
          }
          
          annotationReportData.push({
            annotationId: annotation.id,
            label: annotation.label,
            position: {
              x: annotation.x,
              y: annotation.y,
              width: annotation.width,
              height: annotation.height
            },
            confidence: annotation.confidence,
            linkedMicroUrl: annotation.linkedMicroUrl,
            linkedMicroId: annotation.linkedMicroId,
            abcdeAssessments: abcdeReportData,
            segmentation: segmentationData,
            riskLevel: latestRiskLevel,
            lastAssessmentDate: abcdeReportData.length > 0 
              ? abcdeReportData[abcdeReportData.length - 1].assessmentDate 
              : undefined
          });
        }
        
        macroImageReportData.push({
          imageId: macroImage.id,
          imageUrl: macroImage.imageUrl || macroImage.filePath,
          filePath: macroImage.filePath || macroImage.imageUrl,
          annotations: annotationReportData
        });
      }
      
      // Determine session type
      const isFollowup = baselineSessions.length > 0 && 
        baselineSessions.some((baseline: { createdAt: string }) => new Date(baseline.createdAt) < new Date(session.createdAt));
      
      sessionReportData.push({
        sessionId: session.id,
        sessionDate: session.sessionDate,
        sessionType: isFollowup ? 'followup' : 'baseline',
        notes: session.notes,
        macroImages: macroImageReportData,
        comparison: undefined // TODO: Add comparison data if available
      });
    }
    
    // Generate recommendations based on findings
    const recommendations = generateRecommendations(sessionReportData, Array.from(riskTrends.values()));
    
    // Sort sessions by date (newest first)
    sessionReportData.sort((a, b) => new Date(b.sessionDate).getTime() - new Date(a.sessionDate).getTime());
    
    const reportData: PatientReportData = {
      patientId,
      patientName: `Patient ${patientId}`, // TODO: Get actual patient name from API
      reportDate: new Date().toISOString(),
      summary: {
        totalSessions: sessions.length,
        totalMoles: totalMoles,
        totalABCDEAssessments: totalABCDEAssessments,
        highRiskMoles: highRiskMoles,
        followupSessions: sessionReportData.filter(s => s.sessionType === 'followup').length,
        lastSessionDate: sessionReportData.length > 0 ? sessionReportData[0].sessionDate : ''
      },
      sessions: sessionReportData,
      riskTrends: Array.from(riskTrends.values()),
      recommendations
    };
    
    console.log('Generated comprehensive patient report:', reportData);
    return reportData;
    
  } catch (error) {
    console.error('Failed to generate patient report:', error);
    throw error;
  }
}

// Add new function for backend PDF generation with better error handling
export async function generatePatientReportPDF(patientId: string): Promise<Blob> {
  try {
    console.log(`Generating PDF report for patient: ${patientId}`);
    
    // First get the comprehensive report data
    console.log('Fetching report data...');
    const reportData = await getPatientReportData(patientId);
    console.log('Report data fetched:', {
      totalSessions: reportData.summary.totalSessions,
      totalMoles: reportData.summary.totalMoles,
      sessionsCount: reportData.sessions.length
    });
    
    // Send to backend for PDF generation
    const headers = await getAuthHeaders();
    console.log('Sending PDF generation request to backend...');
    
    const response = await axios.post(
      `${API_BASE}/reports/patient-report-pdf/${patientId}`, 
      reportData,
      { 
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        responseType: 'blob', // Important for PDF download
        timeout: 30000 // 30 second timeout
      }
    );
    
    console.log('PDF generation response received:', {
      status: response.status,
      contentType: response.headers['content-type'],
      size: response.data.size
    });
    
    return response.data;
    
  } catch (error: any) {
    console.error('Failed to generate PDF report:', error);
    
    if (error.response) {
      console.error('Error response status:', error.response.status);
      console.error('Error response data:', error.response.data);
      
      // Try to read error message from blob if it's text
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          console.error('Error blob content:', text);
        } catch (blobError) {
          console.error('Could not read blob error:', blobError);
        }
      }
    }
    
    throw error;
  }
}

// Helper functions
async function getMacroImagesForSession(sessionId: string) {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/molemapping/${sessionId}/images`, { headers });
    return response.data;
  } catch (error) {
    console.warn(`Failed to fetch macro images for session ${sessionId}:`, error);
    return [];
  }
}

async function getAnnotationsForMacroImage(macroImageId: string) {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/molemapping/annotations/${macroImageId}`, { headers });
    return response.data;
  } catch (error) {
    console.warn(`Failed to fetch annotations for macro image ${macroImageId}:`, error);
    return [];
  }
}

function getRiskLevel(totalScore: number): 'low' | 'moderate' | 'high' {
  if (totalScore <= 2) return 'low';
  if (totalScore <= 4) return 'moderate';
  return 'high';
}

function calculateTrend(assessments: ABCDEReportData[]): 'stable' | 'improving' | 'worsening' {
  if (assessments.length < 2) return 'stable';
  
  const sorted = assessments.sort((a, b) => new Date(a.assessmentDate).getTime() - new Date(b.assessmentDate).getTime());
  const first = sorted[0].scores.total;
  const last = sorted[sorted.length - 1].scores.total;
  
  if (last > first + 1) return 'worsening';
  if (last < first - 1) return 'improving';
  return 'stable';
}

function generateRecommendations(sessions: SessionReportData[], riskTrends: RiskTrendData[]): string[] {
  const recommendations: string[] = [];
  
  // Check for high risk moles
  const highRiskMoles = sessions.flatMap(s => 
    s.macroImages.flatMap(img => 
      img.annotations.filter(ann => ann.riskLevel === 'high')
    )
  );
  
  if (highRiskMoles.length > 0) {
    recommendations.push(`Immediate dermatologist consultation recommended for ${highRiskMoles.length} high-risk lesion(s).`);
  }
  
  // Check for worsening trends
  const worseningTrends = riskTrends.filter(trend => trend.trend === 'worsening');
  if (worseningTrends.length > 0) {
    recommendations.push(`${worseningTrends.length} lesion(s) showing worsening ABCDE scores - increased monitoring frequency recommended.`);
  }
  
  // Check follow-up timing
  const lastSession = sessions.length > 0 ? sessions[0] : null;
  if (lastSession) {
    const daysSinceLastSession = Math.floor((new Date().getTime() - new Date(lastSession.sessionDate).getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysSinceLastSession > 180) { // 6 months
      recommendations.push('Follow-up mole mapping session due (>6 months since last assessment).');
    }
  }
  
  // Check for moles without recent ABCDE assessment
  const molesWithoutRecentABCDE = sessions.flatMap(s => 
    s.macroImages.flatMap(img => 
      img.annotations.filter(ann => 
        !ann.lastAssessmentDate || 
        Math.floor((new Date().getTime() - new Date(ann.lastAssessmentDate).getTime()) / (1000 * 60 * 60 * 24)) > 90
      )
    )
  );
  
  if (molesWithoutRecentABCDE.length > 0) {
    recommendations.push(`${molesWithoutRecentABCDE.length} lesion(s) require updated ABCDE assessment.`);
  }
  
  // General recommendations
  if (sessions.length === 1) {
    recommendations.push('Establish baseline with follow-up session in 3-6 months.');
  }
  
  if (recommendations.length === 0) {
    recommendations.push('Continue routine monitoring. Next assessment recommended in 6-12 months.');
  }
  
  return recommendations;
}
